import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import React, { useRef, useState } from 'react';

import { GoogleLookupField, GoogleLookupUnavailable } from './GoogleLookup';
import { AddressValue, labelSx, parseServiceAddress, valueSx } from './LocationHelpers';
import { ManualAddressForm } from './ManualAddressForm';
import { AddressDataType } from '../../../../../components/organisms/StructureAddress';
import { GOOGLE_MAPS_API_KEY } from '../../../../../configs/envs';

export { MapSquare } from './MapSquare';

const toggleBtnSx = (active: boolean) => ({
  fontSize: '11px',
  fontWeight: active ? 600 : 400,
  px: 1.5,
  py: 0.5,
  cursor: 'pointer',
  borderRadius: '4px',
  bgcolor: active ? 'v4.accent' : 'transparent',
  color: active ? 'common.white' : 'text.secondary',
  border: 'none',
  fontFamily: 'inherit',
  '&:hover': active ? {} : { bgcolor: 'v4.tableHover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 1 },
});

const triggerBtnSx = {
  ...valueSx,
  display: 'block',
  width: '100%',
  textAlign: 'left' as const,
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: 'inherit',
  fontFamily: 'inherit',
  cursor: 'pointer',
  '&:hover': { bgcolor: 'v4.tableHover', borderRadius: 1, px: 0.5, mx: -0.5 },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'v4.accent',
    outlineOffset: 2,
    borderRadius: 1,
  },
};

const cancelBtnSx = {
  fontSize: '11px',
  px: 1.5,
  py: 0.5,
  border: 'none',
  bgcolor: 'transparent',
  cursor: 'pointer',
  color: 'text.secondary',
  fontFamily: 'inherit',
  borderRadius: '4px',
  '&:hover': { color: 'text.primary' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 1 },
};

const saveBtnSx = {
  fontSize: '11px',
  fontWeight: 600,
  px: 2,
  py: 0.5,
  border: 'none',
  borderRadius: '4px',
  bgcolor: 'v4.accent',
  color: 'common.white',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { opacity: 0.9 },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'v4.accent', outlineOffset: 2 },
};

const EditorFooter: React.FC<{ onCancel: () => void; onSave: () => void }> = ({
  onCancel,
  onSave,
}) => (
  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1.5 }}>
    <Box component="button" type="button" onClick={onCancel} sx={cancelBtnSx}>
      Cancel
    </Box>
    <Box component="button" type="button" onClick={onSave} sx={saveBtnSx}>
      Save
    </Box>
  </Box>
);

type EditMode = 'google' | 'manual';

/** Strip Google-only metadata from a payload so manual saves don't leak it. */
const stripGoogleMetadata = (fields: AddressValue): AddressValue => {
  const result = { ...fields };
  delete result.googleAddress;
  delete result.googleResponse;
  return result;
};

/** Floating address editor with Google Places autocomplete + manual entry. */
export const EditableAddress: React.FC<{
  serviceAddress: unknown;
  displayValue: string;
  onSave?: (value: Record<string, unknown>) => void;
}> = ({ serviceAddress, displayValue, onSave }) => {
  const googleAvailable = Boolean(GOOGLE_MAPS_API_KEY);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<EditMode>(googleAvailable ? 'google' : 'manual');
  const [fields, setFields] = useState<AddressValue>(() => parseServiceAddress(serviceAddress));

  const handleOpen = () => {
    setFields(parseServiceAddress(serviceAddress));
    setMode(googleAvailable ? 'google' : 'manual');
    setOpen(true);
  };

  const handleSave = () => {
    setOpen(false);
    if (!onSave) return;
    const isManual = mode === 'manual';
    const hasGoogleSelection = !isManual && Boolean(fields.googleAddress);
    if (!isManual && !hasGoogleSelection) return;
    const payload = isManual ? stripGoogleMetadata(fields) : fields;
    onSave({
      ...payload,
      isManual,
      countryName: payload.countryName ?? 'Australia',
      countryId: payload.countryId ?? 1,
      addressType: 'Service',
    });
  };

  const handleCancel = () => setOpen(false);

  const updateField = (key: keyof AddressValue, val: string) =>
    setFields((prev) => ({ ...prev, [key]: val }));

  const handleGoogleSelect = (next: AddressDataType) => {
    setFields((prev) => ({
      ...prev,
      line1: next.line1 ?? prev.line1,
      line2: next.line2 ?? prev.line2,
      suburb: next.suburb ?? prev.suburb,
      city: next.city ?? prev.city,
      stateName: next.stateName ?? prev.stateName,
      postCode: next.postCode ?? prev.postCode,
      countryName: next.countryName ?? prev.countryName,
      countryId: next.countryId ?? prev.countryId,
      googleAddress: next.googleAddress,
      googleResponse: next.googleResponse,
    }));
  };

  const label = displayValue || '—';

  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={labelSx}>Service Address</Typography>
      {onSave ? (
        <Box
          component="button"
          type="button"
          ref={anchorRef}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={handleOpen}
          sx={triggerBtnSx}
        >
          {label}
        </Box>
      ) : (
        <Typography sx={valueSx}>{label}</Typography>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            elevation={8}
            sx={{
              width: 400,
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
              <Box
                component="button"
                type="button"
                disabled={!googleAvailable}
                sx={{
                  ...toggleBtnSx(mode === 'google'),
                  opacity: googleAvailable ? 1 : 0.4,
                  cursor: googleAvailable ? 'pointer' : 'not-allowed',
                }}
                onClick={() => googleAvailable && setMode('google')}
              >
                Google Lookup
              </Box>
              <Box
                component="button"
                type="button"
                sx={toggleBtnSx(mode === 'manual')}
                onClick={() => setMode('manual')}
              >
                Manual Entry
              </Box>
            </Box>

            {mode === 'google' ? (
              googleAvailable ? (
                <GoogleLookupField fields={fields} onSelect={handleGoogleSelect} />
              ) : (
                <GoogleLookupUnavailable />
              )
            ) : (
              <ManualAddressForm fields={fields} updateField={updateField} />
            )}

            <EditorFooter onCancel={handleCancel} onSave={handleSave} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
